import { viewerStrings } from '../copy/strings.js';
import { message, hasText, optionalText } from '../copy/text.js';
import { useEffect, useState, useSyncExternalStore } from 'react';
import Alert from '@cloudscape-design/components/alert';
import type { GroupReference } from '@cdr-clarity/contracts';
import type { RecordsState } from '../state/records-state.js';
import { groupRecords, type DrillDown } from '../state/group-records.js';
import type { RecordPage } from '../state/record-page.js';
import { cellHelp, cellText, fieldTitle } from '../presentation/cells.js';

export function ReportDrilldown({
  reference,
  store,
  drillDown,
  close,
}: {
  reference: GroupReference;
  store: RecordsState;
  drillDown: DrillDown;
  close(): void;
}) {
  const fields = useSyncExternalStore(store.fields.subscribe, store.fields.getSnapshot);
  const state = useSyncExternalStore(store.subscribe, store.getSnapshot);
  const [page, setPage] = useState<RecordPage | null>(null);
  const [cursors, setCursors] = useState<readonly (readonly string[] | null)[]>([null]);
  const [index, setIndex] = useState(0);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(true);
  useEffect(() => {
    let active = true;
    setBusy(true);
    setError('');
    setPage(null);
    void groupRecords(drillDown, reference, fields.visible, cursors[index]!)
      .then((result) => {
        if (active) setPage(result);
      })
      .catch((reason: unknown) => {
        if (active) setError(String(reason));
      })
      .finally(() => {
        if (active) setBusy(false);
      });
    return () => {
      active = false;
    };
  }, [drillDown, reference, fields.visible, state.generation, cursors, index]);
  const visible = fields.visible.filter((field) =>
    field.collections.includes(reference.query.scope.collection),
  );
  return (
    <section
      className="report-drilldown"
      aria-label={optionalText(viewerStrings.webComponentsReportDrilldown.reportSourceRecords)}
      aria-busy={busy}
    >
      <div className="field-tools" data-copy-group>
        {hasText(viewerStrings.webComponentsReportDrilldown.reportSourceRecords2) ? (
          <strong>{viewerStrings.webComponentsReportDrilldown.reportSourceRecords2}</strong>
        ) : null}
        {hasText(viewerStrings.webComponentsReportDrilldown.closeRecords) ? (
          <button data-testid="drilldown-close" onClick={close}>
            {viewerStrings.webComponentsReportDrilldown.closeRecords}
          </button>
        ) : null}
      </div>
      {hasText(error) && <Alert type="error">{error}</Alert>}
      {busy &&
        (hasText(viewerStrings.webComponentsReportDrilldown.loadingSourceRecords) ? (
          <p role="status">{viewerStrings.webComponentsReportDrilldown.loadingSourceRecords}</p>
        ) : null)}
      {page && (
        <>
          {hasText(viewerStrings.webComponentsReportDrilldown.sourceRecordsReportMemberships) ? (
            <p>
              {message(viewerStrings.webComponentsReportDrilldown.sourceRecordsReportMemberships, {
                value1: page.result.totalRecords.toLocaleString(),
                value2: ' ',
                value3: page.result.totalMemberships.toLocaleString(),
              })}
            </p>
          ) : null}
          <div className="report-table-overflow">
            <table>
              <thead>
                <tr>
                  {visible.map((field) => (
                    <th key={field.id}>{fieldTitle(field, fields.preference.timezone)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {page.result.records.map((record, row) => (
                  <tr key={`${record.id}:${record.role ?? ''}:${row}`}>
                    {visible.map((field) => (
                      <td
                        key={field.id}
                        title={optionalText(
                          cellHelp(record.cells[field.id], field, fields.preference.timezone),
                        )}
                      >
                        {cellText(record.cells[field.id], field, fields.preference.timezone)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="field-tools" data-copy-group>
            {hasText(viewerStrings.webComponentsReportDrilldown.previousRecords) ? (
              <button
                data-testid="drilldown-previous"
                disabled={busy || index === 0}
                onClick={() => setIndex(index - 1)}
              >
                {viewerStrings.webComponentsReportDrilldown.previousRecords}
              </button>
            ) : null}
            {hasText(viewerStrings.webComponentsReportDrilldown.page) ? (
              <span>
                {message(viewerStrings.webComponentsReportDrilldown.page, { value1: index + 1 })}
              </span>
            ) : null}
            {hasText(viewerStrings.webComponentsReportDrilldown.nextRecords) ? (
              <button
                data-testid="drilldown-next"
                disabled={busy || !page.nextCursors}
                onClick={() => {
                  setCursors([...cursors.slice(0, index + 1), page.nextCursors]);
                  setIndex(index + 1);
                }}
              >
                {viewerStrings.webComponentsReportDrilldown.nextRecords}
              </button>
            ) : null}
          </div>
        </>
      )}
    </section>
  );
}
